import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import ImageUploader from "@/components/admin/ImageUploader";
import {
  listProgetti,
  createProgetto,
  updateProgetto,
  deleteProgetto,
} from "@/lib/api";

const CATEGORIE = ["Finestre", "Porte Finestre", "Legno", "Installazione"];

const EMPTY = {
  titolo: "",
  categorie: ["Finestre"],
  descrizione: "",
  data_installazione: "",
  cover_image_url: "",
  immagini: [],
  sort_order: 0,
};

export default function ProgettiPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = new, obj = edit
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => {
    setLoading(true);
    setError("");
    listProgetti()
      .then(setItems)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      titolo: p.titolo || "",
      categorie: p.categorie && p.categorie.length > 0
        ? p.categorie
        : (p.categoria ? p.categoria.split(",").map((c) => c.trim()).filter(Boolean) : ["Finestre"]),
      descrizione: p.descrizione || "",
      data_installazione: p.data_installazione || "",
      cover_image_url: p.cover_image_url || "",
      immagini: p.immagini || [],
      sort_order: p.sort_order || 0,
    });
    setDialogOpen(true);
  };

  const toggleCategoria = (cat) => {
    setForm((f) => {
      const has = f.categorie.includes(cat);
      const next = has ? f.categorie.filter((c) => c !== cat) : [...f.categorie, cat];
      // Non permettere lista vuota: mantieni almeno una categoria
      return { ...f, categorie: next.length > 0 ? next : f.categorie };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Se non è impostata cover, usa la prima immagine
      const cover = form.cover_image_url || form.immagini[0]?.image_url || "";
      const payload = { ...form, cover_image_url: cover };
      if (editing) {
        await updateProgetto(editing.id, payload);
      } else {
        await createProgetto(payload);
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      setError(err.message || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteProgetto(deleteTarget.id);
      setDeleteTarget(null);
      load();
    } catch (err) {
      setError(err.message || "Errore eliminazione");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#0B1E36]">Progetti</h2>
          <p className="text-sm text-[#64748B]">
            {items.length} {items.length === 1 ? "progetto" : "progetti"}
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-[#0B1E36] hover:bg-[#0B1E36]/90"
          data-testid="progetti-create-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuovo progetto
        </Button>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-[#0B1E36]" />
        </div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center text-[#64748B]">
          Nessun progetto ancora. Clicca "Nuovo progetto" per iniziare.
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <Card key={p.id} className="overflow-hidden">
              <div className="aspect-video bg-[#F1F5F9] overflow-hidden">
                {p.cover_image_url ? (
                  <img
                    src={p.cover_image_url}
                    alt={p.titolo}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#94A3B8] text-sm">
                    Nessuna cover
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="flex flex-wrap gap-1 mb-2">
                  {(p.categorie && p.categorie.length > 0
                    ? p.categorie
                    : p.categoria ? [p.categoria] : []
                  ).map((cat) => (
                    <span
                      key={cat}
                      className="text-[10px] px-2 py-0.5 bg-[#FF9500]/10 text-[#FF9500] font-semibold uppercase rounded-full"
                    >
                      {cat}
                    </span>
                  ))}
                </div>
                <h3 className="font-bold text-[#0B1E36] mb-1">{p.titolo}</h3>
                {p.descrizione && (
                  <p className="text-sm text-[#64748B] line-clamp-2 mb-3">
                    {p.descrizione}
                  </p>
                )}
                <div className="text-xs text-[#94A3B8] mb-3">
                  {p.immagini?.length || 0} immagini in galleria
                  {p.data_installazione && (
                    <> · installato {p.data_installazione}</>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEdit(p)}
                    className="flex-1"
                    data-testid={`progetto-edit-${p.id}`}
                  >
                    <Pencil className="w-3 h-3 mr-1" />
                    Modifica
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setDeleteTarget(p)}
                    className="text-red-600 hover:bg-red-50 hover:text-red-700"
                    data-testid={`progetto-delete-${p.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog create/edit */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Modifica progetto" : "Nuovo progetto"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="titolo">Titolo *</Label>
              <Input
                id="titolo"
                value={form.titolo}
                onChange={(e) => setForm({ ...form, titolo: e.target.value })}
                required
                data-testid="progetto-form-titolo"
              />
            </div>

            <div>
              <Label>Categorie * <span className="text-xs font-normal text-[#94A3B8]">(selezionane una o più)</span></Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                {CATEGORIE.map((c) => {
                  const checked = form.categorie.includes(c);
                  return (
                    <label
                      key={c}
                      className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                        checked
                          ? "border-[#0B1E36] bg-[#0B1E36]/5"
                          : "border-[#E2E8F0] hover:border-[#94A3B8]"
                      }`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleCategoria(c)}
                        data-testid={`progetto-form-categoria-${c.toLowerCase().replace(/\s+/g, "-")}`}
                      />
                      <span className="text-sm font-medium text-[#0B1E36]">{c}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <Label htmlFor="descrizione">Descrizione</Label>
              <Textarea
                id="descrizione"
                value={form.descrizione}
                onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
                rows={3}
                data-testid="progetto-form-descrizione"
              />
            </div>

            <div>
              <Label htmlFor="data_installazione">Data installazione</Label>
              <Input
                id="data_installazione"
                type="date"
                value={form.data_installazione}
                onChange={(e) => setForm({ ...form, data_installazione: e.target.value })}
                data-testid="progetto-form-data"
              />
            </div>

            <div>
              <Label>Immagini galleria</Label>
              <p className="text-xs text-[#64748B] mb-2">
                La prima immagine sarà usata come copertina. Trascina per riordinare.
              </p>
              <ImageUploader
                images={form.immagini}
                onChange={(imgs) =>
                  setForm({
                    ...form,
                    immagini: imgs,
                    cover_image_url:
                      form.cover_image_url && imgs.some((i) => i.image_url === form.cover_image_url)
                        ? form.cover_image_url
                        : imgs[0]?.image_url || "",
                  })
                }
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                disabled={saving}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                className="bg-[#0B1E36] hover:bg-[#0B1E36]/90"
                disabled={saving}
                data-testid="progetto-form-save"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvataggio…
                  </>
                ) : editing ? (
                  "Salva modifiche"
                ) : (
                  "Crea progetto"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare "{deleteTarget?.titolo}"?</AlertDialogTitle>
            <AlertDialogDescription>
              L'operazione è irreversibile. Verranno eliminate anche tutte le
              immagini della galleria dal server.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
